package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.gestures.detectHorizontalDragGestures
import androidx.compose.foundation.BorderStroke
import androidx.compose.material.icons.filled.SwipeLeft
import androidx.compose.material.icons.filled.SwipeRight
import androidx.compose.ui.input.pointer.pointerInput
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService
import com.karyadina.service.PlatformFileHelper
import com.karyadina.ui.theme.FlashcardColors
import com.karyadina.ui.theme.FlashcardIcons
import com.karyadina.ui.theme.StudyPalette
import kotlinx.coroutines.launch


// TODO: Phase 3 - Move state management to VM
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UnitDetailScreen(
    unit: StudyUnit,
    subject: FlashCard_Subject,
    service: FlashcardService,
    onBack: () -> Unit,
    onSelectCardForReview: (com.karyadina.model.Flashcard) -> Unit = {},
    onStudyAll: () -> Unit = {},
    onStudyDue: () -> Unit = {}  // NEW: Callback for due-only study
) {
    var cards by remember { mutableStateOf(service.getCards(unit.id)) }
    var showAddCardDialog by remember { mutableStateOf(false) }
    var showExportScreen by remember { mutableStateOf(false) }
    var reviewType by remember { mutableStateOf("") }  // Track review type: "due" or "all"
    
    val dueCards = remember(cards) { 
        service.getDueCards(subject.id, unit.id)
    }
    val listState = androidx.compose.foundation.lazy.rememberLazyListState()
    val fabVisible by remember {
        androidx.compose.runtime.derivedStateOf {
            listState.firstVisibleItemIndex == 0
        }
    }
    
    val scope = rememberCoroutineScope()
    
    // Use Scaffold for action buttons (parent uses Box, so no nesting conflict)
    Scaffold(
        floatingActionButton = {
            // Show FAB when cards exist and at top of list
            androidx.compose.animation.AnimatedVisibility(
                visible = cards.isNotEmpty() && fabVisible,
                enter = androidx.compose.animation.slideInVertically(
                    initialOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeIn(),
                exit = androidx.compose.animation.slideOutVertically(
                    targetOffsetY = { it * 2 }
                ) + androidx.compose.animation.fadeOut()
            ) {
                ExtendedFloatingActionButton(
                    onClick = { showAddCardDialog = true },
                    icon = { Icon(Icons.Filled.Add, "Add") },
                    text = { Text("Add Card") },
                    containerColor = MaterialTheme.colorScheme.primary
                )
            }
        }
    ) { padding ->
        if (cards.isEmpty()) {
            EmptyCardsState(
                unitName = unit.name,
                onAddCard = { showAddCardDialog = true },
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
            )
        } else {
            LazyColumn(
                state = listState,
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Progress Card
                item {
                    UnitProgressCard(
                        unit = unit,
                        totalCards = cards.size,
                        dueCards = dueCards.size,
                        masteredCards = cards.count { it.masteryLevel == "MASTERED" },
                        newCards = cards.count { it.masteryLevel == "NEW" },
                        learningCards = cards.count { it.masteryLevel == "LEARNING" },
                        reviewCards = cards.count { it.masteryLevel == "REVIEW" },
                        onStudyDue = onStudyDue,
                        onStudyAll = onStudyAll,
                        onExportPdf = { showExportScreen = true }
                    )
                }
                
                items(cards.size) { index ->
                    val card = cards[index]
                    FlashcardItem(
                        card = card,
                        subjectColor = subject.color,
                        onToggleFavorite = {
                            service.toggleFavorite(card.id)
                            cards = service.getCards(unit.id)
                        },
                        onDelete = {
                            service.deleteCard(card.id)
                            cards = service.getCards(unit.id)
                        },
                        onReviewThisCard = {
                            onSelectCardForReview(card)
                        },
                        itemIndex = index
                    )
                }
            }
        }
    }
    
    if (showAddCardDialog) {
            AddFlashcardDialog(
                onDismiss = { showAddCardDialog = false },
                onAdd = { q: String, a: String, h: String?, tags: List<String>, images: List<String>, hasContent: Boolean, fl: String?, fd: String?, ft: String? ->
                    scope.launch {
                        // Persist images to internal storage
                        val savedImagePaths = mutableListOf<String>()
                        for (uri in images) {
                            PlatformFileHelper.saveImageToAppStorage(uri)?.let { savedImagePaths.add(it) }
                        }

                        if (fl != null) {
                            service.createCardWithFormula(
                                unitId = unit.id,
                                subjectId = subject.id,
                                question = q,
                                answer = a,
                                hint = h,
                                tags = tags,
                                imagePaths = savedImagePaths,
                                formulaLatex = fl,
                            formulaDescription = fd,
                            formulaVariables = emptyMap(), // TODO: Extract variables if available
                            formulaImagePath = savedImagePaths.lastOrNull(), // Use the last scanned image as the formula image
                            formulaType = ft
                            )
                        } else {
                            service.createCard(
                                unitId = unit.id,
                                subjectId = subject.id,
                                question = q,
                                answer = a,
                                hint = h,
                                tags = tags,
                                imagePaths = savedImagePaths,
                                hasScannedContent = hasContent
                            )
                        }
                        cards = service.getCards(unit.id)
                        showAddCardDialog = false
                    }
                }
            )
        }
        
        if (showExportScreen) {
            ExportNotesToPdfScreen(
                subject = subject,
                unit = unit,
                service = service,
                onBack = { showExportScreen = false },
                onExportComplete = { path ->
                    showExportScreen = false
                }
            )
        }
    }

@Composable
fun UnitProgressCard(
    unit: StudyUnit,
    totalCards: Int,
    dueCards: Int,
    masteredCards: Int,
    newCards: Int,
    learningCards: Int,
    reviewCards: Int,
    onStudyDue: () -> Unit,
    onStudyAll: () -> Unit,
    onExportPdf: () -> Unit = {}
) {
    var isExpanded by remember { mutableStateOf(true) }
    
    // ===== PREMIUM ANIMATIONS =====
    // Smooth progress animation (1200ms)
    val animatedProgress by animateFloatAsState(
        targetValue = if (totalCards > 0) masteredCards.toFloat() / totalCards else 0f,
        animationSpec = tween(durationMillis = 1200, easing = EaseInOutCubic),
        label = "progressAnimation"
    )
    
    // Scale feedback when progress changes
    val progressScale by animateFloatAsState(
        targetValue = 1f,
        animationSpec = spring(dampingRatio = 0.6f, stiffness = 500f),
        label = "progressScale"
    )
    
    // Continuous clock rotation when due cards exist
    val clockRotation by animateFloatAsState(
        targetValue = if (dueCards > 0) 360f else 0f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 8000, easing = LinearEasing),
            repeatMode = RepeatMode.Restart
        ),
        label = "clockRotation"
    )
    
    // Clock color animation (Red if due, Green if all good)
    val clockColor by animateColorAsState(
        targetValue = if (dueCards > 0) MaterialTheme.colorScheme.error else StudyPalette.Green500,
        animationSpec = tween(durationMillis = 600),
        label = "clockColorAnimation"
    )
    
    // Collapse arrow rotation
    val arrowRotation by animateFloatAsState(
        targetValue = if (isExpanded) 0f else 180f,
        animationSpec = tween(durationMillis = 300),
        label = "arrowRotation"
    )
    
    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer {
                scaleY = progressScale
            },
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f)
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    brush = Brush.linearGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.08f),
                            MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.03f)
                        )
                    )
                )
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Premium Header with animated clock icon (Samsung One UI style)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Animated clock icon
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .background(
                                color = clockColor.copy(alpha = 0.15f),
                                shape = CircleShape
                            )
                            .graphicsLayer {
                                rotationZ = clockRotation
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            Icons.Filled.Schedule,
                            contentDescription = if (dueCards > 0) "Has due cards" else "All scheduled",
                            tint = clockColor,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                    
                    Column {
                        Text(
                            "Your Progress",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                        Text(
                            if (dueCards > 0) "Ready for review!" else "All caught up",
                            style = MaterialTheme.typography.labelSmall,
                            color = clockColor,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                }
                
                // Animated progress number
                Text(
                    "${masteredCards}/${totalCards}",
                    style = MaterialTheme.typography.displaySmall.copy(fontSize = 20.sp),
                    fontWeight = FontWeight.ExtraBold,
                    color = MaterialTheme.colorScheme.primary,
                    textAlign = TextAlign.Center
                )
            }
            
            // Due cards alert (Premium style)
            if (dueCards > 0) {
                Surface(
                    shape = RoundedCornerShape(12.dp),
                    color = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.6f),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(12.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Filled.NotificationsActive,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(18.dp)
                        )
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                "$dueCards cards ready for review",
                                style = MaterialTheme.typography.labelMedium,
                                fontWeight = FontWeight.SemiBold,
                                color = MaterialTheme.colorScheme.error
                            )
                            Text(
                                "Start reviewing now to stay on track",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onErrorContainer.copy(alpha = 0.7f)
                            )
                        }
                    }
                }
            }
            
            // Premium Animated Progress Bar
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        "Mastery Progress",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                    Text(
                        "${(animatedProgress * 100).toInt()}%",
                        style = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
                
                // Animated progress bar with gradient
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(8.dp)
                        .clip(RoundedCornerShape(4.dp)),
                    color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxHeight()
                            .fillMaxWidth(animatedProgress)
                            .background(
                                brush = Brush.horizontalGradient(
                                    colors = listOf(
                                        StudyPalette.Green500,
                                        StudyPalette.Green500.copy(alpha = 0.7f)
                                    )
                                )
                            )
                    )
                }
            }
            
            // Mastery Distribution - Premium animatable chips
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                MasteryLevelChip("New", newCards, FlashcardColors.getMasteryColor("NEW"), FlashcardIcons.getMasteryIcon("NEW"), isAnimated = true)
                MasteryLevelChip("Learning", learningCards, FlashcardColors.getMasteryColor("LEARNING"), FlashcardIcons.getMasteryIcon("LEARNING"), isAnimated = true)
                MasteryLevelChip("Review", reviewCards, FlashcardColors.getMasteryColor("REVIEW"), FlashcardIcons.getMasteryIcon("REVIEW"), isAnimated = true)
                MasteryLevelChip("Mastered", masteredCards, FlashcardColors.getMasteryColor("MASTERED"), FlashcardIcons.getMasteryIcon("MASTERED"), isAnimated = true)
            }
            
            // Premium Divider
            Divider(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
                thickness = 1.dp
            )
            
            // Actions - Premium Swipeable Review Button
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 8.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (dueCards > 0 || masteredCards < totalCards) {
                    SwipeReviewButton(
                        dueCards = dueCards,
                        totalCards = totalCards,
                        masteredCards = masteredCards,
                        onReviewDue = { onStudyDue() },
                        onStudyAll = { onStudyAll() },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                
                // Export button
                if (totalCards > 0) {
                    Surface(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(44.dp),
                        shape = RoundedCornerShape(10.dp),
                        color = MaterialTheme.colorScheme.surface,
                        border = BorderStroke(
                            width = 1.5.dp,
                            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
                        )
                    ) {
                        Button(
                            onClick = onExportPdf,
                            modifier = Modifier.fillMaxSize(),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = MaterialTheme.colorScheme.surface
                            ),
                            elevation = ButtonDefaults.buttonElevation(
                                defaultElevation = 0.dp
                            )
                        ) {
                            Icon(
                                Icons.Filled.Share,
                                "Export",
                                tint = MaterialTheme.colorScheme.primary,
                                modifier = Modifier.size(18.dp)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                "Export PDF",
                                color = MaterialTheme.colorScheme.primary,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SwipeReviewButton(
    dueCards: Int,
    totalCards: Int,
    masteredCards: Int,
    onReviewDue: () -> Unit,
    onStudyAll: () -> Unit,
    modifier: Modifier = Modifier
) {
    var swipeOffset by remember { mutableStateOf(0f) }
    var swipeFeedback by remember { mutableStateOf("neutral") }
    
    val animatedOffset by animateFloatAsState(
        targetValue = swipeOffset,
        animationSpec = spring(dampingRatio = 0.75f, stiffness = 300f),
        label = "swipeOffset"
    )
    
    val feedbackColor = when (swipeFeedback) {
        "left" -> MaterialTheme.colorScheme.error
        "right" -> StudyPalette.Green500
        else -> MaterialTheme.colorScheme.primary
    }
    
    val feedbackAlpha by animateFloatAsState(
        targetValue = if (swipeFeedback != "neutral") 0.15f else 0.05f,
        animationSpec = tween(300),
        label = "feedbackAlpha"
    )
    
    Surface(
        modifier = Modifier
            .then(modifier)
            .height(52.dp)
            .clip(RoundedCornerShape(12.dp))
            .pointerInput(Unit) {
                detectHorizontalDragGestures(
                    onHorizontalDrag = { change, dragAmount ->
                        change.consume()
                        swipeOffset = (swipeOffset + dragAmount).coerceIn(-200f, 200f)
                        
                        swipeFeedback = when {
                            swipeOffset < -100 -> "left"
                            swipeOffset > 100 -> "right"
                            else -> "neutral"
                        }
                    },
                    onDragEnd = {
                        when (swipeFeedback) {
                            "left" -> {
                                onStudyAll()  // Left = All non-mastered cards
                                swipeOffset = 0f
                            }
                            "right" -> {
                                onReviewDue()  // Right = Due cards only
                                swipeOffset = 0f
                            }
                            else -> swipeOffset = 0f
                        }
                        swipeFeedback = "neutral"
                    }
                )
            },
        color = feedbackColor.copy(alpha = feedbackAlpha),
        border = BorderStroke(
            width = 1.5.dp,
            color = feedbackColor.copy(alpha = if (swipeFeedback != "neutral") 0.4f else 0.1f)
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    brush = Brush.horizontalGradient(
                        colors = listOf(
                            MaterialTheme.colorScheme.error.copy(alpha = 0.1f),
                            MaterialTheme.colorScheme.error.copy(alpha = 0.02f)
                        )
                    )
                )
        ) {
            // Center content - Always visible with animated hints
            Box(
                modifier = Modifier
                    .align(Alignment.Center)
                    .fillMaxWidth()
                    .padding(horizontal = 12.dp),
                contentAlignment = Alignment.Center
            ) {
                // Hint text (shown when not swiping)
                AnimatedVisibility(
                    visible = swipeFeedback == "neutral",
                    enter = fadeIn() + scaleIn(initialScale = 0.9f),
                    exit = fadeOut() + scaleOut(targetScale = 0.9f)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Filled.PlayArrow,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.size(20.dp)
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            if (dueCards > 0) "Review Now ($dueCards)" else "Study All",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            "← Swipe →",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.5f),
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                }
                
                // Swiping left indicator
                AnimatedVisibility(
                    visible = swipeFeedback == "left",
                    enter = fadeIn(animationSpec = tween(200)),
                    exit = fadeOut(animationSpec = tween(200))
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .graphicsLayer { translationX = animatedOffset },
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Filled.SwipeLeft,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(20.dp)
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            "All Study Cards",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }
                
                // Swiping right indicator  
                AnimatedVisibility(
                    visible = swipeFeedback == "right",
                    enter = fadeIn(animationSpec = tween(200)),
                    exit = fadeOut(animationSpec = tween(200))
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .graphicsLayer { translationX = animatedOffset },
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            "Due Only ($dueCards)",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = StudyPalette.Green500
                        )
                        Spacer(Modifier.width(8.dp))
                        Icon(
                            Icons.Filled.SwipeRight,
                            contentDescription = null,
                            tint = StudyPalette.Green500,
                            modifier = Modifier.size(20.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun MasteryLevelChip(
    label: String,
    count: Int,
    color: Color,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    isAnimated: Boolean = false
) {
    // Animated scale when count changes
    val countScale by animateFloatAsState(
        targetValue = 1f,
        animationSpec = spring(dampingRatio = 0.5f, stiffness = 400f),
        label = "chipScale"
    )
    
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(6.dp),
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .background(color = color.copy(alpha = 0.1f))
            .padding(10.dp)
            .graphicsLayer {
                scaleX = countScale
                scaleY = countScale
            }
    ) {
        Surface(
            shape = CircleShape,
            color = color.copy(alpha = 0.2f),
            modifier = Modifier.size(40.dp)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(
                        brush = Brush.radialGradient(
                            colors = listOf(
                                color.copy(alpha = 0.15f),
                                color.copy(alpha = 0.05f)
                            )
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    icon,
                    contentDescription = null,
                    tint = color,
                    modifier = Modifier.size(20.dp)
                )
            }
        }
        Text(
            count.toString(),
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.ExtraBold,
            color = color
        )
        Text(
            label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            maxLines = 1
        )
    }
}

@Composable
fun EmptyCardsState(
    unitName: String,
    onAddCard: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        Icon(
            Icons.Filled.Style,
            contentDescription = null,
            modifier = Modifier.size(100.dp),
            tint = MaterialTheme.colorScheme.primary.copy(alpha = 0.3f)
        )
        Spacer(modifier = Modifier.height(24.dp))
        Text(
            "No Flashcards Yet",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold
        )
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            "Create flashcards for $unitName",
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
        )
        Spacer(modifier = Modifier.height(32.dp))
        Button(
            onClick = onAddCard,
            modifier = Modifier.size(width = 200.dp, height = 56.dp)
        ) {
            Icon(Icons.Filled.Add, contentDescription = null)
            Spacer(modifier = Modifier.width(8.dp))
            Text("Add Card", style = MaterialTheme.typography.titleMedium)
        }
    }
}
